import { wait } from '@yp/common/async.ts';
import { log } from '@yp/common/core.ts';
import { isAbsent, isArray, isPresent, type ParametersOf } from '@yp/common/identity.ts';
import type { Annotated, MaybeAnnotated } from '@yp/common/schema/annotations.ts';
import { type Scalar, type Time } from '@yp/common/units.ts';
import { DEV } from 'esm-env';
import {
  derived,
  readable,
  writable,
  type Readable,
  type StartStopNotifier,
  type Unsubscriber,
} from 'svelte/store';

export type Stores =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | Readable<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | [Readable<any>, ...Array<Readable<any>>]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | Array<Readable<any>>;

export type StoresValues<T> =
  T extends Readable<infer U>
    ? U
    : {
        [K in keyof T]: T[K] extends Readable<infer U> ? U : never;
      };

export interface Loadable<T> extends Readable<T> {
  load(timeout?: Scalar<Time>): Promise<T>;
}

export type Setter<T> = (v: T) => void;

const debug = DEV && false;

// DNS: Unit test
class Loader<T> {
  private static nextDebugId = 1;
  private resolver!: (value: T) => void;
  private loading = new Promise<T>((resolve) => (this.resolver = resolve));
  private loaded = false;
  private waiters = 0;
  private readonly debugId_ = debug ? Loader.nextDebugId++ : 0;
  get debugId() {
    return isPresent(this.annotation) ? `${this.debugId_}:${this.annotation}` : `${this.debugId_}`;
  }

  constructor(
    private readonly resetValue: T | undefined,
    private readonly annotation: string | undefined,
  ) {}

  set(set: Setter<T>) {
    return (value: T) => {
      if (debug) {
        log(
          'Loader.set - got value',
          {
            debugId: this.debugId,
            loaded: this.loaded,
            waiters: this.waiters,
            value,
          },
          { verbosity: 'terse' },
        );
      }
      set(value);
      if (debug) {
        log('Loader.set - resolving', {
          debugId: this.debugId,
          loaded: this.loaded,
          waiters: this.waiters,
        });
      }
      if (this.loaded) {
        this.loading = Promise.resolve(value);
      } else {
        this.loaded = true;
        this.resolver(value);
      }
      if (debug) {
        log('Loader.set - done', {
          debugId: this.debugId,
          loaded: this.loaded,
          waiters: this.waiters,
        });
      }
    };
  }

  reset(set: Setter<T>) {
    return () => {
      if (debug) {
        log('Loader.reset', {
          debugId: this.debugId,
          loaded: this.loaded,
        });
      }
      if (this.loaded) {
        this.loading = new Promise<T>((resolve) => (this.resolver = resolve));
        this.loaded = false;
        // debugger;
        set(this.resetValue as T);
      }
      if (debug) {
        log('Loader.reset - done', {
          debugId: this.debugId,
          loaded: this.loaded,
        });
      }
    };
  }

  load(readable: Readable<T>) {
    // N.B. Once loaded, this should always return the same Promise, or else we'll spuriously
    // re-evaulate on every potential change, but not actual change. Example:
    // {#await loadable.load() then}
    //   ... <this code should not be re-evaluated after the first load> ...
    // {/await}
    // Reference: https://github.com/sveltejs/svelte/pull/3438

    return (timeout?: Scalar<Time>) => {
      if (debug) {
        this.waiters += 1;
        log('Loader.load - subscribing', { debugId: this.debugId, waiters: this.waiters });
      }
      // Subscribe until the first `set()`, then unsubscribe.
      // TODO: Worth subscribing if pending already is ready?
      // DNS: This won't return the latest version on load if no one is subscribed. Write a test.
      const unsub = readable.subscribe((value) => {
        if (debug) {
          // This is purely for observation. Note that values that come in outside of a call to
          // set() do not change loaded state. Regardless, we can't tell here whether this *is*
          // part of a set. Arguably, this debug output is more misleading than helpful.
          log(
            'Loader.load - got value',
            { debugId: this.debugId, waiters: this.waiters, value, loaded: this.loaded },
            { verbosity: 'terse' },
          );
        }
      });
      const work = isPresent(timeout)
        ? Promise.race([wait(timeout).then(() => undefined), this.loading])
        : this.loading;
      const complete = work.then((value) => {
        // void this.loading.then((_v) => {
        if (debug) {
          log('Loader.load - unsubscribing', {
            debugId: this.debugId,
            value,
            waiters: this.waiters,
          });
        }
        // TODO: Consider deferring this for a tick in case our caller immediately re-subscribes.
        unsub();
        // });
        if (debug) {
          this.waiters -= 1;
          log('Loader.load - done', { debugId: this.debugId, value, waiters: this.waiters });
        }
        if (isAbsent(value)) {
          throw new Error('load() timeout');
        }
        return value;
      });
      // This always needs to be the same Promise (unless reset).
      // Note that we can't return the same one if there is a timeout.
      return isPresent(timeout) ? complete : this.loading;
    };

    // return async () => {
    //   if (debug) {
    //     this.waiters += 1;
    //     log('Loader.load - subscribing', { waiters: this.waiters });
    //   }
    //   // TODO: Worth subscribing if pending already is ready?
    //   // DNS: This won't return the latest version on load if no one is subscribed. Write a test.
    //   const unsub = readable.subscribe(() => {
    //     if (debug) {
    //       log('Loader.load - got value', { waiters: this.waiters });
    //     }
    //   });
    //   await this.loading;
    //   if (debug) {
    //     log('Loader.load - unsubscribing', { waiters: this.waiters });
    //   }
    //   // TODO: Consider deferring this for a tick in case our caller immediately re-subscribes.
    //   unsub();
    //   if (debug) {
    //     this.waiters -= 1;
    //     log('Loader.load - done', { waiters: this.waiters });
    //   }
    //   return this.value as T;
    // };
  }
}

export function loaded<T>(value: T, annotation?: string) {
  const result = readable(value);
  return { ...result, load: () => Promise.resolve(value), annotation };
}

export function loadable<T>(
  start: StartStopNotifier<T>,
  initialValue?: T,
  annotation?: string,
): Annotated<Loadable<T>> {
  annotation ??= 'loadable';
  const recorder = new Loader<T>(initialValue, annotation);
  // DNS: Wrap update.
  const result = readable<T>(initialValue, (set, update) => start(recorder.set(set), update));
  return Object.freeze({ ...result, load: recorder.load(result), annotation });
}

export function settableLoadable<T>(
  initialValue?: T,
  annotation?: string,
): {
  loadable: Annotated<Loadable<T>>;
  set: (value: T) => void;
} {
  annotation ??= 'settableLoadable';
  const recorder = new Loader<T>(initialValue, annotation);
  const result = writable<T>(initialValue);
  const loadable = Object.freeze({
    ...result,
    load: recorder.load(result),
    subscribe: (...params: ParametersOf<typeof result.subscribe>) => {
      if (debug) {
        log('Loader.subscribe', { debugId: recorder.debugId });
      }
      const unsub = result.subscribe(...params);
      if (debug) {
        return () => {
          log('Loader.unsubscribe', { debugId: recorder.debugId });
          unsub();
        };
      } else {
        return unsub;
      }
    },
    annotation,
  });
  return { loadable, set: recorder.set(result.set) };
}

// TODO: Consider an overload that maps instead of async sets.
export function derivedLoadable<S extends Stores, T>(
  stores: S,
  fn: (values: StoresValues<S>, set: (value: T) => void, reset: () => void) => Unsubscriber | void,
  initialValue?: T,
  annotation?: string,
): Annotated<Loadable<T>> {
  // TODO: Wait for loadables within `values` to minimize exposure to undefined.
  if (debug) {
    if (isAbsent(annotation)) {
      const deps = (isArray(stores) ? stores : [stores]) as MaybeAnnotated<unknown>[];
      // TODO: functional annotations for when dependent annotations can change.
      annotation = `fn(${deps.map((d) => d.annotation ?? `?`).join(',')})`;
    }
  } else {
    annotation ??= 'derivedLoadable';
  }
  const recorder = new Loader<T>(initialValue, annotation);
  const result = derived<S, T>(
    stores,
    (values, set) => fn(values, recorder.set(set), recorder.reset(set)),
    initialValue,
  );
  return Object.freeze({ ...result, load: recorder.load(result), annotation });
}
